import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import SportFilter from '../components/shared/SportFilter';
import { toggleBetSlip } from '../utils/betSlip';
import { useLang } from '../i18n';

const API = process.env.REACT_APP_API_BASE_URL;

const css = `
  .fb-leagues { display: flex; gap: 8px; padding: 10px 14px 8px; overflow-x: auto; flex-shrink: 0; }
  .fb-leagues::-webkit-scrollbar { display: none; }
  .fb-league-btn { display: flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 20px; border: 1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.03); color: #a0a8b8; font-size: 12px; font-weight: 600; white-space: nowrap; cursor: pointer; transition: all .2s; flex-shrink: 0; }
  .fb-league-btn.active { border-color: rgba(0,212,170,.6); background: rgba(0,212,170,.1); color: #00d4aa; }
  .fb-league-btn img { width: 16px; height: 16px; object-fit: contain; border-radius: 2px; }
  .fb-league-btn .fb-live-dot { width: 7px; height: 7px; border-radius: 50%; background: #ff3b3b; flex-shrink: 0; }
  .fb-show-more { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 50%; border: 1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.03); color: #5a5972; cursor: pointer; flex-shrink: 0; font-size: 16px; font-weight: 700; margin: auto 0; }

  .fb-list { padding: 6px 14px 80px; }

  .fb-card { background: rgba(255,255,255,.02); border: 1px solid rgba(255,255,255,.06); border-radius: 14px; margin-bottom: 10px; overflow: hidden; cursor: pointer; transition: border-color .2s; }
  .fb-card:active { border-color: rgba(0,212,170,.3); }

  .fb-card-head { display: flex; align-items: center; justify-content: space-between; padding: 7px 12px; border-bottom: 1px solid rgba(255,255,255,.04); }
  .fb-card-league { display: flex; align-items: center; gap: 6px; }
  .fb-card-league img { width: 14px; height: 14px; object-fit: contain; }
  .fb-card-league-name { font-size: 11px; color: #5a5972; font-weight: 600; }
  .fb-card-date { font-size: 11px; color: #5a5972; }
  .fb-card-live-badge { font-size: 10px; font-weight: 800; color: #ff3b3b; background: rgba(255,59,59,.12); padding: 2px 7px; border-radius: 10px; }

  .fb-card-body { display: flex; align-items: center; padding: 10px 12px; gap: 10px; }
  .fb-teams { flex: 1; display: flex; flex-direction: column; gap: 6px; min-width: 0; }
  .fb-team { display: flex; align-items: center; gap: 8px; }
  .fb-team img { width: 20px; height: 20px; object-fit: contain; flex-shrink: 0; }
  .fb-team-name { font-size: 13px; font-weight: 600; color: #e4e7ec; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .fb-odds { display: flex; gap: 5px; flex-shrink: 0; }
  .fb-odd-btn { display: flex; flex-direction: column; align-items: center; padding: 7px 8px; min-width: 42px; border-radius: 8px; border: 1px solid rgba(255,255,255,.07); background: rgba(255,255,255,.03); cursor: pointer; transition: all .15s; }
  .fb-odd-btn.selected { border-color: rgba(0,212,170,.6); background: rgba(0,212,170,.12); }
  .fb-odd-btn:active { opacity: .7; }
  .fb-odd-label { font-size: 10px; color: #5a5972; font-weight: 700; margin-bottom: 2px; }
  .fb-odd-value { font-size: 12px; color: #e4e7ec; font-weight: 700; }
  .fb-odd-btn.selected .fb-odd-label, .fb-odd-btn.selected .fb-odd-value { color: #00d4aa; }
  .fb-no-odds { font-size: 11px; color: #3a3857; padding: 8px 0; }

  .fb-spinner { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; gap: 14px; }
  .fb-spinner-ring { width: 36px; height: 36px; border: 3px solid rgba(255,255,255,.06); border-top-color: #00d4aa; border-radius: 50%; animation: fb-spin 1s linear infinite; }
  @keyframes fb-spin { to { transform: rotate(360deg); } }
  .fb-spinner p { color: #5a5972; font-size: 13px; margin: 0; }
  .fb-empty { display: flex; align-items: center; justify-content: center; padding: 60px 20px; }
  .fb-empty p { color: #3a3857; font-size: 13px; }
`;

function getParisDateTime() {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).formatToParts(new Date());
  const g = t => p.find(x => x.type === t)?.value || '00';
  return { date: `${g('year')}-${g('month')}-${g('day')}`, sec: parseInt(g('hour')) * 3600 + parseInt(g('minute')) * 60 + parseInt(g('second')) };
}
function timeSec(s) { if (!s) return 0; const [h, m, sec] = s.split(':').map(Number); return h * 3600 + m * 60 + (sec || 0); }
function isMatchLive(match) {
  if (match.status === 'finished') return false;
  if (match.status === 'live') return true;
  if (!match.livestart) return false;
  const { date: today, sec: nowSec } = getParisDateTime();
  const matchDate = match.date ? match.date.split('T')[0] : null;
  if (matchDate && matchDate !== today) return false;
  const start = timeSec(match.livestart);
  const end = match.endTime ? timeSec(match.endTime) : start + 7200;
  return nowSec >= start && nowSec < end;
}

function fmtDate(iso) {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function fmtTime(timeStr) {
  return timeStr ? timeStr.slice(0, 5) : '';
}

function parseOdds(fixture) {
  const mw = [];
  if (fixture.odds?.match_winner?.length > 0) {
    fixture.odds.match_winner.forEach((o, i) => {
      const map = {
        Home: { label: '1', betType: 'Match Winner: Home', index: i },
        Draw: { label: 'X', betType: 'Match Winner: Draw', index: i + 3 },
        Away: { label: '2', betType: 'Match Winner: Away', index: i + 6 },
      };
      const m = map[o.label];
      if (m) mw.push({ ...m, value: o.odd });
    });
  }
  return mw;
}

function parseInternalOdds(match) {
  const mr = match.odds?.matchResult;
  if (!mr) return [];
  return [
    { label: '1', betType: 'Match Winner: Home', value: parseFloat(mr.teamA).toFixed(2), index: 0 },
    { label: 'X', betType: 'Match Winner: Draw', value: parseFloat(mr.draw).toFixed(2),  index: 1 },
    { label: '2', betType: 'Match Winner: Away', value: parseFloat(mr.teamB).toFixed(2), index: 2 },
  ].filter(o => !isNaN(parseFloat(o.value)));
}

export default function Football() {
  const { t } = useLang();
  const [leagues, setLeagues]             = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [fixtures, setFixtures]           = useState([]);
  const [visible, setVisible]             = useState([]);
  const [loadLeagues, setLoadLeagues]     = useState(true);
  const [loadFixtures, setLoadFixtures]   = useState(false);
  const [visibleCount, setVisibleCount]   = useState(10);
  const [visibleLeagues, setVisibleLeagues] = useState(5);
  const [selBets, setSelBets]             = useState([]);
  const loadMoreRef = useRef();
  const obsRef      = useRef();
  const reqLeagueRef = useRef(null);
  const userInteractedRef = useRef(false);
  const navigate    = useNavigate();

  useEffect(() => {
    const sync = () => setSelBets(JSON.parse(localStorage.getItem('betSlipItems') || '[]').map(b => b.id));
    sync();
    window.addEventListener('betAdded', sync);
    return () => window.removeEventListener('betAdded', sync);
  }, []);

  useEffect(() => { fetchLeagues(); }, []);

  // Switch league: clear the list synchronously so a stale-shaped list is never
  // rendered through the wrong card template (prevents "? / N/A" cards & black screen).
  const selectLeague = (l) => {
    if (l.id === selectedLeague?.id) return;
    userInteractedRef.current = true; // l'utilisateur a choisi : stoppe l'auto-sélection
    setVisible([]);
    setFixtures([]);
    setVisibleCount(10);
    setLoadFixtures(true);
    setSelectedLeague(l);
  };

  useEffect(() => {
    if (!selectedLeague) return;
    setVisibleCount(10);
    setFixtures([]);
    fetchFixtures(selectedLeague);
  }, [selectedLeague]);

  useEffect(() => { setVisible(fixtures.slice(0, visibleCount)); }, [fixtures, visibleCount]);

  const loadMore = useCallback(() => {
    if (visibleCount < fixtures.length) setVisibleCount(p => p + 10);
  }, [visibleCount, fixtures.length]);

  useEffect(() => {
    if (obsRef.current) obsRef.current.disconnect();
    obsRef.current = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { threshold: 0.1 });
    if (loadMoreRef.current) obsRef.current.observe(loadMoreRef.current);
    return () => { if (obsRef.current) obsRef.current.disconnect(); };
  }, [loadMore]);

  const fetchLeagues = async () => {
    setLoadLeagues(true);
    try {
      const [apiRes, internalRes] = await Promise.all([
        (async () => {
          // _v2 : la structure des ligues inclut désormais `season` (chantier 2).
          const k = 'football_leagues_v2';
          const c = localStorage.getItem(k);
          if (c) {
            const { data, ts } = JSON.parse(c);
            if (Date.now() - ts < 30 * 864e5) return { cached: true, data };
          }
          const res = await fetch(`${API}/api-bet/football/leagues.php`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
          });
          return { cached: false, raw: await res.json() };
        })(),
        fetch(`${API}/matchs/read_all.php`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
        }).then(r => r.json()).catch(() => null),
      ]);

      let list = [];

      if (apiRes.cached) {
        list = apiRes.data;
      } else if (apiRes.raw?.success) {
        list = [...(apiRes.raw.top_leagues || []), ...(apiRes.raw.other_leagues || [])].map(l => ({
          id: l.league.id, name: l.league.name, logo: l.league.logo, country: l.country?.name,
          season: l.seasons?.[0]?.year,
        }));
        localStorage.setItem('football_leagues_v2', JSON.stringify({ data: list, ts: Date.now() }));
      }

      // Group internal matches by championship/league name
      const internalMatches = internalRes?.matches || internalRes?.championships?.flatMap(c => c.matches || []) || [];
      const grouped = {};
      internalMatches.forEach(m => {
        const name = m.league?.name || m.league || m.championship || 'Venezuela D2';
        if (!grouped[name]) grouped[name] = [];
        grouped[name].push(m);
      });
      const internalLeagues = Object.entries(grouped).map(([name, matches]) => ({
        id: `internal:${name}`, name, logo: null, internal: true, matches,
      }));

      const allLeagues = [...list.slice(0, 3), ...internalLeagues, ...list.slice(3)];
      setLeagues(allLeagues);
      if (allLeagues.length) autoSelectBestLeague(allLeagues);
    } catch (e) { console.error(e); } finally { setLoadLeagues(false); }
  };

  // Chantier 3 : ne pas atterrir sur une ligue vide. On sonde les ligues dans
  // l'ordre et on sélectionne la 1re qui a des matchs. userInteractedRef coupe
  // court dès que l'utilisateur clique une ligue lui-même.
  const autoSelectBestLeague = async (leaguesList) => {
    if (leaguesList.length) setLoadFixtures(true); // spinner pendant le sondage (évite un flash "aucun match")
    const candidates = leaguesList.slice(0, 12);
    for (const lg of candidates) {
      if (userInteractedRef.current) return;
      const data = await loadFixturesData(lg);
      if (userInteractedRef.current) return;
      if (data.length > 0) { setSelectedLeague(lg); return; }
    }
    if (!userInteractedRef.current && leaguesList.length > 0) setSelectedLeague(leaguesList[0]);
  };

  // Récupère les matchs d'une ligue SANS toucher au state (réutilisable par
  // l'auto-sélection ET par fetchFixtures). Peuple le cache au passage.
  const loadFixturesData = async (league) => {
    if (league.internal) return league.matches || [];
    const season = league.season;
    const k = `football_fixtures_${league.id}_${season || 'def'}`;
    try {
      const c = localStorage.getItem(k);
      if (c) {
        const { data, ts } = JSON.parse(c);
        if (Date.now() - ts < 36e5) {
          const v = data.filter(f => f.fixture.timestamp * 1000 > Date.now());
          if (v.length) return v;
        }
      }
    } catch {}
    try {
      const url = `${API}/api-bet/football/matchs.php?league=${league.id}${season ? `&season=${season}` : ''}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        const v = (data.fixtures || []).filter(f => f.fixture.timestamp * 1000 > Date.now());
        localStorage.setItem(k, JSON.stringify({ data: v, ts: Date.now() }));
        return v;
      }
    } catch (e) { console.error(e); }
    return [];
  };

  const fetchFixtures = async (league) => {
    reqLeagueRef.current = league.id;
    setLoadFixtures(true);
    const v = await loadFixturesData(league);
    // Ignore une réponse pour une ligue déjà quittée par l'utilisateur.
    if (reqLeagueRef.current !== league.id) return;
    setFixtures(v);
    setLoadFixtures(false);
  };

  const handleOdd = (e, fixture, betType, oddLabel, oddValue, oddIndex) => {
    e.stopPropagation();
    const [market] = betType.split(':');
    toggleBetSlip({
      id: `${fixture.fixture.id}-${oddIndex}`,
      matchId: fixture.fixture.id,
      fixtureId: fixture.fixture.id,
      match: `${fixture.teams.home.name} vs ${fixture.teams.away.name}`,
      homeTeam: fixture.teams.home.name,
      awayTeam: fixture.teams.away.name,
      date: new Date(fixture.fixture.date).toLocaleString('fr-FR'),
      fixtureDate: fixture.fixture.date,
      selection: betType,
      betType: market.trim(),
      betLabel: oddLabel,
      handicap: null,
      odds: parseFloat(oddValue),
      league: fixture.league.name,
      leagueId: fixture.league.id,
      isLive: fixture.fixture?.status?.short !== 'NS',
      sport: 'soccer',
    });
  };

  const handleInternalOdd = (e, match, betType, oddValue, oddIndex) => {
    e.stopPropagation();
    const [market] = betType.split(':');
    const label = betType.includes('Home') ? '1' : betType.includes('Draw') ? 'X' : '2';
    toggleBetSlip({
      id: `live-${match.id}-match-${oddIndex}`,
      matchId: match.id,
      match: `${match.teamA?.name || '?'} vs ${match.teamB?.name || '?'}`,
      homeTeam: match.teamA?.name || '?',
      awayTeam: match.teamB?.name || '?',
      date: match.date ? new Date(match.date).toLocaleDateString('fr-FR') : '',
      fixtureDate: match.date || null,
      selection: betType,
      betType: market.trim(),
      betLabel: label,
      handicap: null,
      odds: parseFloat(oddValue),
      league: match.league?.name || match.league || match.championship || selectedLeague?.name || '',
      isLive: true,
      sport: 'football',
    });
  };

  const isInternal = selectedLeague?.internal === true;

  return (
    <PhoneShell activeTab="sports">
      <style>{css}</style>
      <BackHeader title="Football" />
      <SportFilter />

      {loadLeagues ? (
        <div className="fb-spinner"><div className="fb-spinner-ring" /><p>{t('sports.loading')}</p></div>
      ) : (
        <div className="fb-leagues">
          {leagues.slice(0, visibleLeagues).map(l => (
            <button
              key={l.id}
              className={'fb-league-btn' + (selectedLeague?.id === l.id ? ' active' : '')}
              onClick={() => selectLeague(l)}
            >
              {l.internal
                ? <span className="fb-live-dot" />
                : l.logo && <img src={l.logo} alt="" />
              }
              {l.name}
            </button>
          ))}
          {visibleLeagues < leagues.length && (
            <button className="fb-show-more" onClick={() => setVisibleLeagues(p => Math.min(p + 5, leagues.length))}>+</button>
          )}
        </div>
      )}

      {loadFixtures ? (
        <div className="fb-spinner"><div className="fb-spinner-ring" /><p>{t('sports.loadingMatches')}</p></div>
      ) : visible.length === 0 && !loadLeagues ? (
        <div className="fb-empty"><p>{t('sports.noUpcoming')}</p></div>
      ) : (
        <div className="fb-list">
          {isInternal ? (
            /* ── internal match cards ── */
            visible.map(match => {
              const odds = parseInternalOdds(match);
              const datePart = match.date ? match.date.split('T')[0].slice(5).split('-').reverse().join('/') : '';
              const timePart = match.livestart ? match.livestart.slice(0, 5) : '';
              const dateStr = datePart && timePart ? `${datePart} ${timePart}` : datePart || timePart;
              return (
                <div key={match.id} className="fb-card" onClick={() => navigate(`/live/${match.id}`)}>
                  <div className="fb-card-head">
                    <div className="fb-card-league">
                      <span className="fb-card-league-name">{match.league?.name || match.league || match.championship || selectedLeague?.name}</span>
                    </div>
                    {isMatchLive(match) && <span className="fb-card-live-badge">LIVE</span>}
                    <span className="fb-card-date">{dateStr}</span>
                  </div>
                  <div className="fb-card-body">
                    <div className="fb-teams">
                      <div className="fb-team">
                        {match.teamA?.logo && <img src={match.teamA.logo} alt="" />}
                        <span className="fb-team-name">{match.teamA?.name || '?'}</span>
                      </div>
                      <div className="fb-team">
                        {match.teamB?.logo && <img src={match.teamB.logo} alt="" />}
                        <span className="fb-team-name">{match.teamB?.name || '?'}</span>
                      </div>
                    </div>
                    {odds.length > 0 ? (
                      <div className="fb-odds" onClick={e => e.stopPropagation()}>
                        {odds.map(o => {
                          const betId = `live-${match.id}-match-${o.index}`;
                          return (
                            <button
                              key={o.index}
                              className={'fb-odd-btn' + (selBets.includes(betId) ? ' selected' : '')}
                              onClick={e => handleInternalOdd(e, match, o.betType, o.value, o.index)}
                            >
                              <span className="fb-odd-label">{o.label}</span>
                              <span className="fb-odd-value">{o.value}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="fb-no-odds">N/A</span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            /* ── external API match cards ── */
            visible.map(fixture => {
              if (!fixture?.fixture) return null;
              const odds = parseOdds(fixture);
              return (
                <div key={fixture.fixture.id} className="fb-card" onClick={() => navigate(`/match/football/${fixture.fixture.id}`)}>
                  <div className="fb-card-head">
                    <div className="fb-card-league">
                      <img src={fixture.league.logo} alt="" />
                      <span className="fb-card-league-name">{fixture.league.name}</span>
                    </div>
                    <span className="fb-card-date">{fmtDate(fixture.fixture.date)}</span>
                  </div>
                  <div className="fb-card-body">
                    <div className="fb-teams">
                      <div className="fb-team">
                        <img src={fixture.teams.home.logo} alt="" />
                        <span className="fb-team-name">{fixture.teams.home.name}</span>
                      </div>
                      <div className="fb-team">
                        <img src={fixture.teams.away.logo} alt="" />
                        <span className="fb-team-name">{fixture.teams.away.name}</span>
                      </div>
                    </div>
                    {odds.length > 0 ? (
                      <div className="fb-odds" onClick={e => e.stopPropagation()}>
                        {odds.map(o => {
                          const id = `${fixture.fixture.id}-${o.index}`;
                          return (
                            <button
                              key={o.index}
                              className={'fb-odd-btn' + (selBets.includes(id) ? ' selected' : '')}
                              onClick={e => handleOdd(e, fixture, o.betType, o.label, o.value, o.index)}
                            >
                              <span className="fb-odd-label">{o.label}</span>
                              <span className="fb-odd-value">{o.value}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="fb-no-odds">N/A</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
          {visibleCount < fixtures.length && (
            <div ref={loadMoreRef} style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="fb-spinner-ring" style={{ width: 24, height: 24, borderWidth: 2 }} />
            </div>
          )}
        </div>
      )}
    </PhoneShell>
  );
}
